import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import DescriptionIcon from "@mui/icons-material/Description";
import { useNavigate } from "react-router-dom";
import { useOrganization } from "../../contexts/OrganizationContext";
import { useReports, useDeleteReport, useReportTargetOrgs } from "../../api/hooks/useReports";
import { ReportStatus } from "../../api/types/dtos/report";
import LoadingSpinner from "../../components/LoadingSpinner";
import CreateReportModal from "./CreateReportModal";
import FarmDetailsModal from "../../components/modal/FarmDetailsModal";
import { PRIVILEGES } from "../../api/types/dtos";
import { useHasPrivilege } from "../../components/authorization";

const STATUS_LABELS: Record<ReportStatus, string> = {
  requested: "Solicitado",
  in_process: "En proceso",
  available: "Disponible",
  failed: "Fallido",
};

const STATUS_COLORS: Record<ReportStatus, "default" | "info" | "success" | "error"> = {
  requested: "default",
  in_process: "info",
  available: "success",
  failed: "error",
};

const ReportOrgsDialog: React.FC<{
  open: boolean;
  reportId: string | null;
  onClose: () => void;
}> = ({ open, reportId, onClose }) => {
  const { data: orgs, isLoading } = useReportTargetOrgs(open ? reportId : null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [farmDetailsOpen, setFarmDetailsOpen] = useState(false);

  const handleOrgClick = useCallback((orgId: string) => {
    setSelectedOrgId(orgId);
    setFarmDetailsOpen(true);
  }, []);

  const handleFarmDetailsClose = useCallback(() => {
    setFarmDetailsOpen(false);
    setSelectedOrgId(null);
  }, []);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Organizaciones del Reporte</Typography>
            <Button onClick={onClose} startIcon={<CloseIcon />}>
              Cerrar
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!isLoading && orgs && (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {orgs.length} organizaci&oacute;n{orgs.length !== 1 ? "es" : ""}
              </Typography>
              {orgs.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography color="textSecondary">No se encontraron organizaciones</Typography>
                </Box>
              ) : (
                <List>
                  {orgs.map((org) => (
                    <ListItem
                      key={org.id}
                      disablePadding
                      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mb: 1 }}
                    >
                      <ListItemButton
                        onClick={() => handleOrgClick(org.id)}
                        sx={{ borderRadius: 1 }}
                      >
                        <ListItemText primary={org.name} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      {selectedOrgId && (
        <FarmDetailsModal
          open={farmDetailsOpen}
          onClose={handleFarmDetailsClose}
          organizationId={selectedOrgId}
        />
      )}
    </>
  );
};

const ReportsListPage: React.FC = () => {
  const { selectedOrganization } = useOrganization();
  const navigate = useNavigate();
  const { hasPrivilege: canCreateReport } = useHasPrivilege(PRIVILEGES.CREATE_REPORT);
  const { hasPrivilege: canDeleteReport } = useHasPrivilege(PRIVILEGES.DELETE_REPORT);
  const { hasPrivilege: canCreateTemplate } = useHasPrivilege(PRIVILEGES.CREATE_REPORT_TEMPLATE);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [orgDialogReportId, setOrgDialogReportId] = useState<string | null>(null);

  const {
    data: reportsData,
    isLoading,
    refetch,
    isRefetching,
  } = useReports({
    page,
    size: rowsPerPage,
    sortBy: "createdAt",
    sortDirection: "DESC",
  });

  const deleteReport = useDeleteReport();

  const hasPending = reportsData?.content?.some(
    (r) => r.status === "requested" || r.status === "in_process"
  );

  // Auto-refresh when there are pending reports
  useEffect(() => {
    if (!hasPending) return;
    const interval = setInterval(() => {
      refetch();
    }, 60000);
    return () => clearInterval(interval);
  }, [hasPending, refetch]);

  const handleDownload = useCallback((url: string, reportName: string) => {
    const safeName = reportName.replace(/[^a-zA-Z0-9._\- ]/g, "_");
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("Download failed");
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", `${safeName}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        window.location.href = url;
      });
  }, []);

  const handleDelete = useCallback(
    async (id: string) => {
      setDeletingId(id);
      try {
        await deleteReport.mutateAsync(id);
      } finally {
        setDeletingId(null);
      }
    },
    [deleteReport]
  );

  if (!selectedOrganization) {
    return (
      <Container maxWidth="lg">
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            Selecciona una organización para ver los reportes.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const reports = reportsData?.content || [];
  const totalElements = reportsData?.totalElements || 0;

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", my: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Reportes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Historial de reportes generados
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => refetch()}
            startIcon={isRefetching ? <CircularProgress size={16} /> : <RefreshIcon />}
            disabled={isRefetching}
          >
            Actualizar
          </Button>
          {canCreateTemplate && (
            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={() => navigate("/challenges/report-builder?mode=template")}
            >
              Crear Plantilla
            </Button>
          )}
          {canCreateReport && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateModalOpen(true)}
            >
              Nuevo Reporte
            </Button>
          )}
        </Box>
      </Box>

      {deleteReport.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al eliminar el reporte.
        </Alert>
      )}

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Plantilla</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Organizaciones</TableCell>
                  <TableCell>
                    <Tooltip title="Cuándo el usuario creó la solicitud de generación del reporte">
                      <span>Fecha de solicitud</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Fecha programada en la que el sistema procesará el reporte">
                      <span>Fecha de generación</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>Solicitado por</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No hay reportes generados aún.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  reports.map((report) => (
                    <TableRow key={report.id} hover>
                      <TableCell>
                        <Typography variant="body2">{report.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {report.templateName ?? "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_LABELS[report.status]}
                          color={STATUS_COLORS[report.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            cursor: "pointer",
                            color: "primary.main",
                            "&:hover": { textDecoration: "underline" },
                          }}
                          onClick={() => setOrgDialogReportId(report.id)}
                        >
                          {report.orgCount ?? report.targetOrganizationCount ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(report.createdAt).toLocaleString("es", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {report.requestedAt
                            ? new Date(report.requestedAt).toLocaleString("es", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {report.requestedByName ?? "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                          {report.status === "available" && report.reportS3Url && (
                            <Tooltip title="Descargar">
                              <IconButton
                                size="small"
                                onClick={() => handleDownload(report.reportS3Url!, report.name)}
                                color="primary"
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDeleteReport && (
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(report.id)}
                                disabled={deletingId === report.id}
                                color="error"
                              >
                                {deletingId === report.id ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
          />
        </Paper>
      )}

      <CreateReportModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          setCreateModalOpen(false);
          refetch();
        }}
      />

      <ReportOrgsDialog
        open={!!orgDialogReportId}
        reportId={orgDialogReportId}
        onClose={() => setOrgDialogReportId(null)}
      />
    </Container>
  );
};

export default ReportsListPage;
